import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Search, Printer, FileDown, Copy, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleInRange,
} from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { financeService } from '@/services/financeService'
import { reportService } from '@/services/reportService'

const VENDOR_LEDGER_COLUMN_ORDER = [
  'date',
  'vendor',
  'voucher',
  'description',
  'project',
  'costCenter',
  'costLocation',
  'ref',
  'notes',
  'debit',
  'credit',
  'balance',
] as const

interface VendorLedgerEntry {
  account: string | null
  customer: string | null
  costCenter: string | null
  costLocation: string | null
  vendor: string | null
  accountCode: string | null
  description: string
  voucher: number
  type: string | null
  debit: number
  credit: number
  balance: number
  project: string | null
  ref: string | null
  department: string | null
  notes: string | null
  createdDate: string
  branchName: string | null
}

export const VendorLedgerReport: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [ledgerData, setLedgerData] = useState<VendorLedgerEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [vendors, setVendors] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [colVis, setColVis] = useState<Record<string, boolean>>(() =>
    initialColumnVisibility([...VENDOR_LEDGER_COLUMN_ORDER]),
  )

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)

  // Get branch ID from user's selected branch
  const selectedBranchId = user?.selectedBranch?.branchId

  // Load vendors when branch is available
  useEffect(() => {
    if (!selectedBranchId) {
      setVendors([])
      setSelectedVendorId(undefined)
      return
    }

    const loadVendors = async () => {
      try {
        const vendorList = await financeService.getAllVendorsForVoucher(selectedBranchId)
        setVendors(vendorList || [])
        // Reset vendor selection to "All Vendors" when branch changes
        setSelectedVendorId(undefined)
      } catch (error: any) {
        setVendors([])
      }
    }
    loadVendors()
  }, [selectedBranchId])

  // Set default dates (current month)
  useEffect(() => {
    const today = new Date()
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    
    setFromDate(formatDateForInput(firstDay))
    setToDate(formatDateForInput(lastDay))
  }, [])

  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleSearch = async (resetPage: boolean = false) => {
    if (!selectedBranchId) {
      toast.error('Please select a branch')
      return
    }

    if (!fromDate || !toDate) {
      toast.error('Please select both from date and to date')
      return
    }

    try {
      setLoading(true)
      // Convert dates to ISO date-time format (YYYY-MM-DDTHH:mm:ss)
      const fromDateTime = `${fromDate}T00:00:00`
      const toDateTime = `${toDate}T23:59:59`
      
      // If vendor is not selected (All Vendors), send vendorId as 0
      const vendorId = selectedVendorId !== undefined ? selectedVendorId : 0
      
      const currentPage = resetPage ? 1 : pageNumber
      if (resetPage) {
        setPageNumber(1)
      }
      
      const response = await reportService.getVendorLedgerReport({
        fromDate: fromDateTime,
        toDate: toDateTime,
        branchId: selectedBranchId,
        pageNumber: currentPage,
        pageSize: pageSize,
        vendorId: vendorId,
      })
      setLedgerData(response.data || [])
      setTotalCount(response.totalRecords || 0)
      const pages = response.totalRecords ? Math.ceil(response.totalRecords / pageSize) : 0
      setTotalPages(pages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load vendor ledger report')
      setLedgerData([])
      setTotalCount(0)
      setTotalPages(0)
    } finally {
      setLoading(false)
    }
  }

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return ledgerData
    const query = searchQuery.toLowerCase()
    return ledgerData.filter((entry) => {
      return (
        (entry.vendor || '').toLowerCase().includes(query) ||
        (entry.description || '').toLowerCase().includes(query) ||
        (entry.ref || '').toLowerCase().includes(query) ||
        (entry.voucher?.toString() || '').includes(query)
      )
    })
  }, [ledgerData, searchQuery])

  // Calculate totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, entry) => {
        acc.debit += entry.debit || 0
        acc.credit += entry.credit || 0
        return acc
      },
      { debit: 0, credit: 0 }
    )
  }, [filteredData])

  // Handle page size change
  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
    // Trigger search with new page size
    if (selectedBranchId && fromDate && toDate) {
      setTimeout(() => handleSearch(true), 0)
    }
  }

  // Handle page change
  useEffect(() => {
    if (selectedBranchId && fromDate && toDate && pageNumber > 1 && ledgerData.length > 0) {
      handleSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber])

  const vendorLedgerColumnMenu = useMemo(
    () => [
      { id: 'date', label: t('reports.date') },
      { id: 'vendor', label: t('reports.vendor') },
      { id: 'voucher', label: t('reports.voucher') },
      { id: 'description', label: t('reports.description') },
      { id: 'project', label: t('reports.project') },
      { id: 'costCenter', label: t('reports.costProfitCenter') },
      { id: 'costLocation', label: t('reports.costLocation') },
      { id: 'ref', label: t('reports.reference') },
      { id: 'notes', label: t('reports.notes') },
      { id: 'debit', label: t('reports.debit') },
      { id: 'credit', label: t('reports.credit') },
      { id: 'balance', label: t('reports.balance') },
    ],
    [t],
  )

  const toggleVendorLedgerColumn = (id: string) => {
    setColVis((p) => ({ ...p, [id]: !p[id] }))
  }

  const vendorOptions: SearchableSelectOption[] = [
    { value: '', label: t('reports.allVendors') },
    ...vendors.map((vendor) => {
      const vendorId = vendor.vendorId || vendor.id || 0
      const vendorName = vendor.vendorNameEn || vendor.vendorNameAr || vendor.nameEn || vendor.nameAr || `Vendor ${vendorId}`
      return {
        value: String(vendorId),
        label: vendorName,
      }
    }),
  ]

  const handleVendorChange = (value: string) => {
    // When "All Vendors" is selected (empty string), set to undefined so we can send 0 to API
    setSelectedVendorId(value ? Number(value) : undefined)
  }

  const handlePrint = () => {
    if (!selectedBranchId || !fromDate || !toDate) {
      toast.error('Please select date range before printing')
      return
    }
    
    // Navigate to print page with query parameters
    const params = new URLSearchParams({
      fromDate,
      toDate,
      branchId: String(selectedBranchId),
    })
    if (selectedVendorId) {
      params.append('vendorId', String(selectedVendorId))
    }
    
    window.open(`/reports/vendor-ledger/print?${params.toString()}`, '_blank')
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Create CSV content
    const headers = [
      'Date',
      'Vendor',
      'Voucher',
      'Description',
      'Project',
      'Cost Profit Center',
      'Cost Location',
      'Reference',
      'Notes',
      'Debit',
      'Credit',
      'Balance',
    ].join(',')

    const rows = filteredData.map(entry => [
      `"${formatDate(entry.createdDate)}"`,
      `"${entry.vendor || ''}"`,
      entry.voucher || '',
      `"${entry.description || ''}"`,
      `"${entry.project || ''}"`,
      `"${entry.costCenter || ''}"`,
      `"${entry.costLocation || ''}"`,
      `"${entry.ref || ''}"`,
      `"${entry.notes || ''}"`,
      `"${formatAmount(entry.debit)}"`,
      `"${formatAmount(entry.credit)}"`,
      `"${formatAmount(entry.balance)}"`,
    ].join(','))

    // Add total row
    const totalRow = [
      '"Total"',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      '""',
      `"${formatAmount(totals.debit)}"`,
      `"${formatAmount(totals.credit)}"`,
      `"${formatAmount(totals.debit - totals.credit)}"`,
    ].join(',')

    const csvContent = [headers, ...rows, totalRow].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    const dateStr = fromDate && toDate ? `${fromDate}_${toDate}` : 'report'
    link.setAttribute('download', `vendor-ledger-${dateStr}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('CSV file downloaded')
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('navigation.vendorLedgerReport')}
        description={t('reports.viewVendorLedgerEntries')}
        action={{
          label: t('reports.print'),
          onClick: handlePrint,
          icon: Printer,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.fromDate')}</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.toDate')}</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('reports.vendor')}</Label>
              <SearchableSelect
                options={vendorOptions}
                value={selectedVendorId ? String(selectedVendorId) : ''}
                onValueChange={handleVendorChange}
                placeholder={t('reports.allVendors')}
                searchPlaceholder={t('reports.searchVendors')}
                emptyMessage={t('reports.noVendorsFound')}
                disabled={!selectedBranchId}
              />
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => handleSearch(true)} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu
                columns={vendorLedgerColumnMenu}
                visibility={colVis}
                onToggle={toggleVendorLedgerColumn}
              />
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading report...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No data found"
                description="Click Search to load vendor ledger report"
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      {colVis.date && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.date')}</TableHead>
                      )}
                      {colVis.vendor && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.vendor')}</TableHead>
                      )}
                      {colVis.voucher && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.voucher')}</TableHead>
                      )}
                      {colVis.description && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.description')}</TableHead>
                      )}
                      {colVis.project && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.project')}</TableHead>
                      )}
                      {colVis.costCenter && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.costProfitCenter')}</TableHead>
                      )}
                      {colVis.costLocation && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.costLocation')}</TableHead>
                      )}
                      {colVis.ref && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.reference')}</TableHead>
                      )}
                      {colVis.notes && (
                        <TableHead className={`font-bold text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('reports.notes')}</TableHead>
                      )}
                      {colVis.debit && <TableHead className="font-bold text-primary text-right">{t('reports.debit')}</TableHead>}
                      {colVis.credit && <TableHead className="font-bold text-primary text-right">{t('reports.credit')}</TableHead>}
                      {colVis.balance && <TableHead className="font-bold text-primary text-right">{t('reports.balance')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((entry, index) => (
                      <TableRow
                        key={index}
                        className="border-b hover:bg-primary/5 transition-colors"
                      >
                        {colVis.date && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(entry.createdDate)}</TableCell>
                        )}
                        {colVis.vendor && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.vendor || '-'}</TableCell>
                        )}
                        {colVis.voucher && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>
                            {entry.voucher && entry.voucher > 0 ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/general-entries/${entry.voucher}/view`, { state: { readOnly: true } })}
                                className="cursor-pointer text-primary hover:underline"
                              >
                                {entry.voucher}
                              </button>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        )}
                        {colVis.description && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.description || '-'}</TableCell>
                        )}
                        {colVis.project && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.project || '-'}</TableCell>
                        )}
                        {colVis.costCenter && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.costCenter || '-'}</TableCell>
                        )}
                        {colVis.costLocation && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.costLocation || '-'}</TableCell>
                        )}
                        {colVis.ref && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.ref || '-'}</TableCell>
                        )}
                        {colVis.notes && (
                          <TableCell className={`px-3 py-3 ${rtl ? 'text-right' : 'text-left'}`}>{entry.notes || '-'}</TableCell>
                        )}
                        {colVis.debit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.debit)}</TableCell>
                        )}
                        {colVis.credit && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.credit)}</TableCell>
                        )}
                        {colVis.balance && (
                          <TableCell className="px-3 py-3 text-right">{formatAmount(entry.balance)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell
                        colSpan={Math.max(1, countVisibleInRange(colVis, VENDOR_LEDGER_COLUMN_ORDER, 0, 9))}
                        className="px-3 py-3 text-right"
                      >
                        Total
                      </TableCell>
                      {colVis.debit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(totals.debit)}</TableCell>
                      )}
                      {colVis.credit && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(totals.credit)}</TableCell>
                      )}
                      {colVis.balance && (
                        <TableCell className="px-3 py-3 text-right">{formatAmount(totals.debit - totals.credit)}</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
